import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { axiosClient } from "../AxiosClient";
import {
  areAllItemsEligible,
  areReassignmentItemsValid,
  createEmptyReassignmentItem,
  formatPaymentDate,
  formatPaymentValue,
  getExecutionResultMessage,
  getExecutionResultTone,
  getReassignmentHttpErrorMessage,
  getReassignmentItemErrors,
  paymentRecordStatusLabel,
  reassignPaymentDocument,
  validatePaymentDocumentReassignment,
} from "./reassignPaymentDocument";

const fieldLabel = (label, index, total) =>
  total > 1 ? `${label} (pago ${index + 1})` : label;

export default function ReasignarPagoModal({ onClose, open }) {
  const [items, setItems] = useState([createEmptyReassignmentItem()]);
  const [showErrors, setShowErrors] = useState(false);
  const [httpError, setHttpError] = useState("");
  const [validationResults, setValidationResults] = useState(null);
  const [executionResults, setExecutionResults] = useState(null);
  const [isValidating, setIsValidating] = useState(false);
  const [isExecuting, setIsExecuting] = useState(false);

  const loading = isValidating || isExecuting;
  const formIsValid = areReassignmentItemsValid(items);
  const allEligible = areAllItemsEligible(validationResults);
  const isDone = Array.isArray(executionResults);

  useEffect(() => {
    if (open) {
      setItems([createEmptyReassignmentItem()]);
      setShowErrors(false);
      setHttpError("");
      setValidationResults(null);
      setExecutionResults(null);
      setIsValidating(false);
      setIsExecuting(false);
    }
  }, [open]);

  const clearServerState = () => {
    setHttpError("");
    setValidationResults(null);
    setExecutionResults(null);
  };

  const handleItemChange = (index, field) => (event) => {
    const value = event.target.value;
    setItems((current) =>
      current.map((item, itemIndex) =>
        itemIndex === index ? { ...item, [field]: value } : item
      )
    );
    clearServerState();
  };

  const handleAddItem = () => {
    if (loading || isDone) {
      return;
    }
    setItems((current) => [...current, createEmptyReassignmentItem()]);
    clearServerState();
  };

  const handleRemoveItem = (index) => {
    if (loading || isDone || items.length === 1) {
      return;
    }
    setItems((current) => current.filter((_, itemIndex) => itemIndex !== index));
    clearServerState();
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  const handleValidate = async (event) => {
    event.preventDefault();

    if (loading || isDone) {
      return;
    }

    setShowErrors(true);

    if (!formIsValid) {
      return;
    }

    setIsValidating(true);
    setHttpError("");
    setValidationResults(null);
    setExecutionResults(null);

    try {
      const results = await validatePaymentDocumentReassignment(
        axiosClient,
        items
      );
      setValidationResults(results);
    } catch (error) {
      setHttpError(
        getReassignmentHttpErrorMessage(
          error,
          "No fue posible validar la reasignación. Intenta nuevamente."
        )
      );
    } finally {
      setIsValidating(false);
    }
  };

  const handleConfirm = async () => {
    if (loading || !allEligible || isDone) {
      return;
    }

    const userUuid = localStorage.getItem("userUuid");
    setIsExecuting(true);
    setHttpError("");

    try {
      const results = await reassignPaymentDocument(
        axiosClient,
        userUuid,
        items
      );
      setExecutionResults(results);
    } catch (error) {
      setHttpError(
        getReassignmentHttpErrorMessage(
          error,
          "No fue posible ejecutar la reasignación. Intenta nuevamente."
        )
      );
    } finally {
      setIsExecuting(false);
    }
  };

  return (
    <Dialog
      aria-labelledby="reassign-payment-dialog-title"
      fullWidth
      maxWidth="md"
      onClose={handleClose}
      open={open}
    >
      <form noValidate onSubmit={handleValidate}>
        <DialogTitle id="reassign-payment-dialog-title">
          Reasignar pago
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Corrige el documento de un pago varado en pendiente de aplicación y
            reintenta aplicarlo. No uses este flujo si el pago ya fue aplicado o
            si el crédito simplemente no tiene cuota pendiente.
          </DialogContentText>
          {httpError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {httpError}
            </Alert>
          )}
          {items.map((item, index) => {
            const errors = showErrors ? getReassignmentItemErrors(item) : {};
            const validation = validationResults?.[index];
            const execution = executionResults?.[index];

            return (
              <Box key={`reassignment-item-${index}`} sx={{ mb: 2 }}>
                {index > 0 && <Divider sx={{ mb: 2 }} />}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: 1,
                  }}
                >
                  <Typography sx={{ fontWeight: 600 }} variant="subtitle2">
                    {items.length > 1 ? `Pago ${index + 1}` : "Datos del pago"}
                  </Typography>
                  {items.length > 1 && !isDone && (
                    <IconButton
                      aria-label={`Quitar pago ${index + 1}`}
                      disabled={loading}
                      onClick={() => handleRemoveItem(index)}
                      size="small"
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
                <Box
                  sx={{
                    display: "grid",
                    gap: 2,
                    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr" },
                    mb: 2,
                  }}
                >
                  <TextField
                    disabled={loading || isDone}
                    error={Boolean(errors.paymentRecordOldId)}
                    fullWidth
                    helperText={errors.paymentRecordOldId || ""}
                    inputProps={{ inputMode: "numeric" }}
                    label={fieldLabel("ID del registro", index, items.length)}
                    onChange={handleItemChange(index, "paymentRecordOldId")}
                    required
                    value={item.paymentRecordOldId}
                  />
                  <TextField
                    disabled={loading || isDone}
                    error={Boolean(errors.originalPayerDocument)}
                    fullWidth
                    helperText={errors.originalPayerDocument || ""}
                    label={fieldLabel("Cédula original", index, items.length)}
                    onChange={handleItemChange(index, "originalPayerDocument")}
                    required
                    value={item.originalPayerDocument}
                  />
                  <TextField
                    disabled={loading || isDone}
                    error={Boolean(errors.correctedPayerDocument)}
                    fullWidth
                    helperText={errors.correctedPayerDocument || ""}
                    label={fieldLabel("Cédula corregida", index, items.length)}
                    onChange={handleItemChange(index, "correctedPayerDocument")}
                    required
                    value={item.correctedPayerDocument}
                  />
                </Box>
                <TextField
                  disabled={loading || isDone}
                  error={Boolean(errors.observation)}
                  fullWidth
                  helperText={
                    errors.observation ||
                    "Queda registrado en las notas del pago para auditoría."
                  }
                  label={fieldLabel(
                    "Motivo de la corrección",
                    index,
                    items.length
                  )}
                  minRows={2}
                  multiline
                  onChange={handleItemChange(index, "observation")}
                  required
                  value={item.observation}
                />
                {validation && !execution && (
                  <ValidationResultAlert result={validation} />
                )}
                {execution && <ExecutionResultAlert result={execution} />}
              </Box>
            );
          })}
          {!isDone && (
            <Button
              disabled={loading}
              onClick={handleAddItem}
              startIcon={<AddIcon />}
              sx={{ mt: 1 }}
              type="button"
            >
              Agregar otro pago
            </Button>
          )}
        </DialogContent>
        <DialogActions>
          <Button disabled={loading} onClick={handleClose}>
            {isDone ? "Cerrar" : "Cancelar"}
          </Button>
          {!isDone && (
            <Button
              disabled={loading}
              type="submit"
              variant={allEligible ? "text" : "contained"}
            >
              {isValidating ? (
                <CircularProgress color="inherit" size={18} />
              ) : (
                "Validar"
              )}
            </Button>
          )}
          {allEligible && !isDone && (
            <Button
              disabled={loading}
              onClick={handleConfirm}
              type="button"
              variant="contained"
            >
              {isExecuting ? (
                <CircularProgress color="inherit" size={18} />
              ) : (
                "Confirmar reasignación"
              )}
            </Button>
          )}
        </DialogActions>
      </form>
    </Dialog>
  );
}

function ValidationResultAlert({ result }) {
  if (result.eligible) {
    const record = result.oldPaymentRecord;
    return (
      <Alert severity="success" sx={{ mt: 2 }}>
        Este pago se puede reasignar.
        {record && (
          <Box component="span" sx={{ display: "block", mt: 0.5 }}>
            Registro #{record.oldId} · {formatPaymentValue(record.paymentValue)}{" "}
            · {formatPaymentDate(record.paymentDate)} ·{" "}
            {paymentRecordStatusLabel(record.status)} · documento{" "}
            {record.payerDocument}
          </Box>
        )}
      </Alert>
    );
  }

  return (
    <Alert severity="error" sx={{ mt: 2 }}>
      {result.reason || "Este pago no se puede reasignar."}
    </Alert>
  );
}

function ExecutionResultAlert({ result }) {
  return (
    <Alert severity={getExecutionResultTone(result)} sx={{ mt: 2 }}>
      {getExecutionResultMessage(result)}
    </Alert>
  );
}
